/**
 * SetIcon Component - MTG set symbol with keyrune font and fallbacks
 *
 * Displays set icons using the keyrune font when available, with graceful
 * fallbacks to Scryfall SVG or text code for unsupported sets.
 *
 * Fallback chain:
 * 1. Keyrune font icon (when set is supported) → rarity colors work
 * 2. Scryfall SVG icon (always available) → no rarity coloring
 * 3. Text code placeholder (last resort)
 */
import clsx from 'clsx';
import type { ReactElement } from 'react';

import type { Rarity } from '@/types/card';

// ============================================================================
// Types
// ============================================================================

export interface SetIconProps {
  /** Set code (e.g., 'neo', 'znr') */
  setCode: string;
  /** Rarity for keyrune coloring */
  rarity?: Rarity;
  /** Scryfall icon SVG URI for fallback */
  scryfallIconUri?: string;
  /** Icon size */
  size?: 'sm' | 'md' | 'lg';
  /** Additional classes */
  className?: string;
  /** Render nothing instead of the `[CODE]` text when no icon is available */
  hideFallback?: boolean;
}

// ============================================================================
// Constants
// ============================================================================

/**
 * All set codes supported by keyrune v3.19.0
 *
 * Generated from keyrune CSS: `.ss-{code}:before` patterns
 * Update this list when upgrading keyrune package
 */
export const KEYRUNE_SUPPORTED_SETS = new Set([
  '10e',
  '1e',
  '2e',
  '2ed',
  '2u',
  '2x2',
  '2xm',
  '30a',
  '3e',
  '3ed',
  '40k',
  '4ed',
  '5dn',
  '5ed',
  '6ed',
  '7ed',
  '8ed',
  '9ed',
  'a25',
  'acr',
  'aer',
  'afc',
  'afr',
  'akh',
  'akr',
  'ala',
  'all',
  'ann',
  'apc',
  'arb',
  'arc',
  'arn',
  'ath',
  'atq',
  'avr',
  'azorius',
  'bbd',
  'bcore',
  'bfz',
  'big',
  'blb',
  'blc',
  'bng',
  'bok',
  'boros',
  'bot',
  'br',
  'brb',
  'brc',
  'bro',
  'brr',
  'btd',
  'c13',
  'c14',
  'c15',
  'c16',
  'c17',
  'c18',
  'c19',
  'c20',
  'c21',
  'cc1',
  'cc2',
  'chk',
  'chr',
  'clb',
  'clu',
  'cm1',
  'cm2',
  'cma',
  'cmc',
  'cmd',
  'cmm',
  'cmr',
  'cn2',
  'cns',
  'con',
  'csp',
  'dd2',
  'ddc',
  'ddd',
  'dde',
  'ddf',
  'ddg',
  'ddh',
  'ddi',
  'ddj',
  'ddk',
  'ddl',
  'ddm',
  'ddn',
  'ddo',
  'ddp',
  'ddq',
  'ddr',
  'dds',
  'ddt',
  'ddu',
  'dft',
  'dgm',
  'dimir',
  'dis',
  'dka',
  'dkm',
  'dmc',
  'dmr',
  'dmu',
  'dom',
  'dpa',
  'drb',
  'drc',
  'drk',
  'dsc',
  'dsk',
  'dst',
  'dtk',
  'duels',
  'dvk',
  'e01',
  'e02',
  'ea1',
  'ecc',
  'ecl',
  'eld',
  'ema',
  'emn',
  'eoc',
  'eoe',
  'eos',
  'eve',
  'evg',
  'exo',
  'exp',
  'fca',
  'fdc',
  'fdn',
  'fem',
  'fic',
  'fin',
  'fra',
  'frf',
  'fut',
  'gk1',
  'gk2',
  'gn2',
  'gn3',
  'gnt',
  'golgari',
  'gpt',
  'grn',
  'gruul',
  'gs1',
  'gtc',
  'h09',
  'h17',
  'ha1',
  'hbg',
  'hml',
  'hob',
  'hoc',
  'hop',
  'hou',
  'htr',
  'htr17',
  'ice',
  'ice2',
  'iko',
  'ima',
  'inr',
  'inv',
  'isd',
  'izzet',
  'j20',
  'j21',
  'j22',
  'j25',
  'j25a',
  'jmp',
  'jou',
  'jud',
  'khc',
  'khm',
  'kld',
  'klr',
  'ktk',
  'lcc',
  'lci',
  'lea',
  'leb',
  'leg',
  'lgn',
  'lrw',
  'ltc',
  'ltr',
  'm10',
  'm11',
  'm12',
  'm13',
  'm14',
  'm15',
  'm19',
  'm20',
  'm21',
  'm3c',
  'mar',
  'mat',
  'mb1',
  'mb2',
  'mbs',
  'md1',
  'me1',
  'me2',
  'me3',
  'me4',
  'med',
  'mh1',
  'mh2',
  'mh3',
  'mic',
  'mid',
  'mir',
  'mkc',
  'mkm',
  'mm2',
  'mm3',
  'mma',
  'mmq',
  'moc',
  'modo',
  'mom',
  'mor',
  'mp1',
  'mp2',
  'mps',
  'mrd',
  'msc',
  'msh',
  'mul',
  'ncc',
  'nec',
  'nem',
  'neo',
  'nms',
  'nph',
  'ody',
  'ogw',
  'om1',
  'omb',
  'onc',
  'one',
  'ons',
  'ori',
  'orzhov',
  'otc',
  'otj',
  'otp',
  'p02',
  'papac',
  'parl',
  'parl2',
  'parl3',
  'past',
  'pbook',
  'pc2',
  'pca',
  'pcy',
  'pd2',
  'pd3',
  'pdep',
  'pdrc',
  'peuro',
  'pfnm',
  'pgru',
  'pheart',
  'pidw',
  'pio',
  'pip',
  'plc',
  'pleaf',
  'pls',
  'pm2',
  'pma',
  'pmei',
  'pmodo',
  'pmps',
  'pmpu',
  'pmtg1',
  'pmtg2',
  'po2',
  'por',
  'psalvat05',
  'psalvat11',
  'psega',
  'psld',
  'psum',
  'ptg',
  'ptk',
  'ptsa',
  'pxbox',
  'pz1',
  'pz2',
  'pza',
  'rakdos',
  'rav',
  'ren',
  'rex',
  'rin',
  'rix',
  'rna',
  'roe',
  'rtr',
  'rvr',
  's00',
  's99',
  'scd',
  'scg',
  'selesnya',
  'shm',
  'simic',
  'sir',
  'sis',
  'sld',
  'sld2',
  'slu',
  'snc',
  'soa',
  'soc',
  'sos',
  'soi',
  'sok',
  'som',
  'spe',
  'spg',
  'spm',
  'ss1',
  'ss2',
  'ss3',
  'sta',
  'sth',
  'stx',
  'td2',
  'tdc',
  'tdm',
  'thb',
  'ths',
  'tla',
  'tle',
  'tmc',
  'tmp',
  'tmt',
  'tor',
  'tpr',
  'tsp',
  'tsr',
  'uds',
  'ugl',
  'ulg',
  'uma',
  'una',
  'und',
  'unf',
  'unh',
  'usg',
  'ust',
  'v09',
  'v0x',
  'v10',
  'v11',
  'v12',
  'v13',
  'v14',
  'v15',
  'v16',
  'v17',
  'van',
  'vis',
  'vma',
  'voc',
  'vow',
  'w16',
  'w17',
  'war',
  'who',
  'woc',
  'woe',
  'wot',
  'wth',
  'wwk',
  'x2ps',
  'x4ea',
  'xcle',
  'xdnd',
  'xduels',
  'xice',
  'xkld',
  'xlcu',
  'xln',
  'xmods',
  'xren',
  'xrin',
  'xssm',
  'y22',
  'y23',
  'y24',
  'y25',
  'y26',
  'yblb',
  'ybro',
  'ydft',
  'ydmu',
  'ydsk',
  'yeoe',
  'ylci',
  'ymid',
  'ymkm',
  'yneo',
  'yone',
  'yotj',
  'ysnc',
  'ytdm',
  'ywoe',
  'zen',
  'znc',
  'zne',
  'znr',
]);

/**
 * Sets that require the ss-duo class for proper rendering
 * (two-layer symbols with inner/outer components)
 */
const DUO_SETS = new Set([
  '40k',
  'afr',
  'afc',
  'c21',
  'clb',
  'cc2',
  'dmc',
  'drc',
  'dsc',
  'ecc',
  'eoc',
  'j21',
  'khc',
  'ltc',
  'm3c',
  'mid',
  'mar',
  'ncc',
  'otc',
  'scd',
  'spm',
  'tla',
  'tle',
  'tmt',
  'una',
  'xdnd',
]);

// Size classes for keyrune icons
const sizeClasses = {
  sm: 'text-sm',
  md: 'text-base',
  lg: 'text-lg',
} as const;

// Size classes for Scryfall SVG fallback
const imgSizeClasses = {
  sm: 'h-3.5 w-3.5',
  md: 'h-4 w-4',
  lg: 'h-5 w-5',
} as const;

// Map rarity to keyrune class
function getRarityClass(rarity?: Rarity): string {
  switch (rarity) {
    case 'common':
      return 'ss-common';
    case 'uncommon':
      return 'ss-uncommon';
    case 'rare':
      return 'ss-rare';
    case 'mythic':
      return 'ss-mythic';
    case 'special':
    case 'bonus':
      return 'ss-timeshifted';
    default:
      return '';
  }
}

// ============================================================================
// Component
// ============================================================================

/**
 * Renders an MTG set icon with keyrune font icons (preferred) or Scryfall SVG fallback
 */
export default function SetIcon({
  setCode,
  rarity,
  scryfallIconUri,
  size = 'md',
  className,
  hideFallback = false,
}: SetIconProps): ReactElement | null {
  const normalizedCode = setCode.toLowerCase();
  const isKeyruneSupported = KEYRUNE_SUPPORTED_SETS.has(normalizedCode);

  if (isKeyruneSupported) {
    const isDuo = DUO_SETS.has(normalizedCode);
    const rarityClass = getRarityClass(rarity);

    return (
      <i
        className={clsx(
          'ss',
          `ss-${normalizedCode}`,
          isDuo && 'ss-duo',
          rarityClass,
          'ss-fw', // Fixed width for alignment
          sizeClasses[size],
          className,
        )}
        role="img"
        aria-label={`${setCode.toUpperCase()} set symbol`}
      />
    );
  }

  // Fallback to Scryfall SVG
  if (scryfallIconUri) {
    return (
      <img
        src={scryfallIconUri}
        alt={`${setCode.toUpperCase()} set`}
        className={clsx(imgSizeClasses[size], 'shrink-0', className)}
        loading="lazy"
        style={{ filter: 'invert(0.7)' }}
      />
    );
  }

  if (hideFallback) return null;

  // Ultimate fallback: text code
  return (
    <span className={clsx('text-ash font-mono text-xs uppercase', className)} aria-label={`${setCode.toUpperCase()} set`}>
      [{setCode.toUpperCase()}]
    </span>
  );
}
